import React, { useEffect, useRef, useState } from 'react';
import { getAuthToken } from '../../services/api';
import axios from 'axios';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Sparkle, PaperPlaneRight, CheckCircle, XCircle, Spinner, Wrench, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${getAuthToken()}` });

const SUGGESTIONS = [
  "Who's working today and for which clients?",
  'Show open shifts for the next two weeks',
  "Change the caregiver for Eleanor Smith's shift tomorrow",
  'Any draft invoices waiting for approval?',
];

const renderText = (t) => (t || '').split(/(\*\*[^*]+\*\*)/g).map((part, i) => part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part);

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`agent-msg-${m.role}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === 'user' ? 'bg-primary text-primary-foreground rounded-br-md' : m.system_event ? (m.content.startsWith('✅') ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' : m.content.startsWith('✖') ? 'bg-slate-100 border border-slate-200 text-slate-700' : 'bg-amber-50 border border-amber-200 text-amber-900') : 'bg-muted rounded-bl-md'}`}>
      {renderText(m.content)}{m.streaming && <span className="inline-block w-1.5 h-4 bg-foreground/60 ml-0.5 animate-pulse align-middle" />}
    </div>
  </div>
);

const ActionCard = ({ a, onResolve }) => {
  const [busy, setBusy] = useState(false);
  const act = async (kind) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/ai/agent/actions/${a.id}/${kind}`, {}, { headers: auth(), withCredentials: true });
      onResolve(a.id, r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  const done = a.status !== 'pending';
  return (
    <div className={`rounded-2xl border p-3 text-sm ${done ? 'border-border bg-muted/40' : 'border-amber-300 bg-amber-50'}`} data-testid={`agent-action-${a.id}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{done ? `Action ${a.status}` : 'Confirm this change'}</p>
      <p className="whitespace-pre-wrap">{a.summary}</p>
      {a.result && <p className="mt-2 text-xs text-muted-foreground">{a.result}</p>}
      {!done && (
        <div className="flex gap-2 mt-3">
          <Button size="sm" className="rounded-xl gap-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act('confirm')} data-testid={`agent-confirm-${a.id}`}><CheckCircle weight="bold" className="w-4 h-4" /> Confirm</Button>
          <Button size="sm" variant="outline" className="rounded-xl gap-1" disabled={busy} onClick={() => act('cancel')} data-testid={`agent-cancel-${a.id}`}><XCircle className="w-4 h-4" /> Cancel</Button>
        </div>
      )}
    </div>
  );
};

const AgentChat = ({ compact = false }) => {
  const [sessionId, setSessionId] = useState(null);
  const [items, setItems] = useState([]); // {kind:'msg'|'action', ...}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [toolName, setToolName] = useState(null);
  const listRef = useRef(null);

  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [items, toolName]);

  const newChat = () => { setSessionId(null); setItems([]); setInput(''); };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput(''); setBusy(true);
    const id = Date.now();
    setItems(prev => [...prev, { kind: 'msg', id: `u${id}`, role: 'user', content: msg }, { kind: 'msg', id: `a${id}`, role: 'assistant', content: '', streaming: true }]);
    try {
      const res = await fetch(`${API_URL}/api/ai/agent/chat`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...auth() }, credentials: 'include', body: JSON.stringify({ message: msg, session_id: sessionId }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split('\n\n'); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith('data: ')) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === 'session') setSessionId(ev.session_id);
          else if (ev.type === 'delta') { setToolName(null); setItems(prev => prev.map(it => it.id === `a${id}` ? { ...it, content: it.content + ev.content } : it)); }
          else if (ev.type === 'tool') setToolName(ev.name);
          else if (ev.type === 'action') setItems(prev => [...prev, { kind: 'action', ...ev.action }]);
          else if (ev.type === 'error') toast.error(ev.message);
        }
      }
    } catch (e) { toast.error(e.message || 'Assistant failed'); }
    finally { setToolName(null); setBusy(false); setItems(prev => prev.map(it => it.id === `a${id}` ? { ...it, streaming: false } : it).filter(it => !(it.id === `a${id}` && !it.content))); }
  };

  const resolve = (actionId, data) => {
    setItems(prev => [...prev.map(it => it.kind === 'action' && it.id === actionId ? { ...it, status: data.status, result: data.result } : it),
      { kind: 'msg', id: `s${Date.now()}`, role: 'assistant', system_event: true, content: (data.status === 'executed' ? '✅ ' : data.status === 'cancelled' ? '✖ ' : '⚠️ ') + (data.result || 'Action cancelled — nothing was changed.') }]);
  };

  return (
    <div className={`flex flex-col ${compact ? 'h-full' : 'h-[calc(100vh-240px)] min-h-[480px]'} rounded-2xl border border-border bg-white overflow-hidden`} data-testid="agent-chat">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2 text-sm font-medium"><Sparkle weight="fill" className="w-4 h-4 text-primary" /> CareGrid Assistant</div>
        <Button variant="ghost" size="sm" className="rounded-xl gap-1 text-xs" onClick={newChat} data-testid="agent-new-chat"><Plus className="w-3.5 h-3.5" /> New chat</Button>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {items.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Ask me to look things up or make changes — I'll always show you exactly what will change and wait for your Confirm.</p>
            <div className="flex flex-wrap gap-2">{SUGGESTIONS.map(s => <button key={s} onClick={() => send(s)} className="text-xs rounded-full border border-border px-3 py-1.5 hover:bg-muted transition-colors" data-testid="agent-suggestion">{s}</button>)}</div>
          </div>
        )}
        {items.map(it => it.kind === 'action' ? <ActionCard key={it.id} a={it} onResolve={resolve} /> : <Bubble key={it.id} m={it} />)}
        {toolName && <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="agent-tool-indicator"><Wrench className="w-3.5 h-3.5 animate-pulse" /> Checking {toolName.replace(/_/g, ' ')}…</p>}
      </div>
      <div className="border-t border-border p-3 flex gap-2 items-end">
        <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder='e.g. "Change the caregiver for Mary Jones from Sue to Jane this Friday"' className="rounded-xl resize-none min-h-[42px] max-h-32" data-testid="agent-input" />
        <Button onClick={() => send()} disabled={busy || !input.trim()} className="rounded-xl h-[42px] px-3" data-testid="agent-send">{busy ? <Spinner className="w-4 h-4 animate-spin" /> : <PaperPlaneRight weight="fill" className="w-4 h-4" />}</Button>
      </div>
    </div>
  );
};

export default AgentChat;
